import React, { useState, useEffect, useMemo, useRef, ChangeEvent } from 'react';
import { Search, X } from 'lucide-react';
import debounce from 'lodash.debounce';
import { Button } from '../Button';
import styles from './SearchBar.module.scss';

export interface SearchBarProps {
    /** Placeholder text for the search input */
    placeholder?: string;
    /** Debounce delay in milliseconds (default: 300) */
    debounceDelay?: number;
    /** Additional CSS class names */
    className?: string;
    /** Callback fired with debounced search value */
    onSearch: (value: string) => void;
    /** Callback fired immediately on input change (optional) */
    onChange?: (value: string) => void;
    /** Callback fired when clear button is clicked (optional) */
    onClear?: () => void;
    /** Controlled value (optional - makes component controlled) */
    value?: string;
    /** Initial value for uncontrolled mode (optional) */
    initialValue?: string;
    /** Test ID for testing purposes */
    testId?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
    placeholder = 'Search...',
    debounceDelay = 300,
    className = '',
    onSearch,
    onChange,
    onClear,
    value: controlledValue,
    initialValue = '',
    testId = 'search-bar',
}) => {
    // Determine if component is controlled
    const isControlled = controlledValue !== undefined;

    // Internal state for uncontrolled mode
    const [internalValue, setInternalValue] = useState<string>(initialValue);

    // Use controlled value if provided, otherwise use internal state
    const inputValue = isControlled ? controlledValue : internalValue;

    // Store onSearch in a ref to avoid recreating debounced function
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    // Create debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((searchValue: string) => {
                onSearchRef.current(searchValue);
            }, debounceDelay),
        [debounceDelay]
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            if (debouncedSearch?.cancel) {
                debouncedSearch.cancel();
            }
        };
    }, [debouncedSearch]);

    // Handle input change
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Update internal state if uncontrolled
        if (!isControlled) {
            setInternalValue(newValue);
        }

        // Call immediate onChange callback if provided
        if (onChange) {
            onChange(newValue);
        }

        // Call debounced search
        debouncedSearch(newValue);
    };

    // Handle clear button click
    const handleClear = () => {
        // Update internal state if uncontrolled
        if (!isControlled) {
            setInternalValue('');
        }

        // Call immediate onChange callback if provided
        if (onChange) {
            onChange('');
        }

        // Cancel any pending debounced search
        debouncedSearch?.cancel();

        // Call onSearch immediately with empty string
        onSearch('');

        // Call onClear callback if provided
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className={`${styles.searchBar} ${className}`} data-testid={testId}>
            <Search className={styles.searchIcon} size={20} aria-hidden='true' data-testid={`${testId}-icon`} />
            <input
                type='text'
                className={styles.searchInput}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleChange}
                data-testid={`${testId}-input`}
                aria-label='Search'
            />
            {inputValue && (
                <Button
                    variant='ghost'
                    size='sm'
                    icon={X}
                    iconOnly
                    onClick={handleClear}
                    className={styles.clearButton}
                    aria-label='Clear search'
                    data-testid={`${testId}-clear-button`}
                />
            )}
        </div>
    );
};

export default SearchBar;
